import { List, ListItem, ListItemIcon, Typography } from '@mui/material';

import FailedIcon from '../../../assets/icons/Failed';
import CheckIcon from '../../../assets/icons/Success';
import colors from '../../../constants/colors';
import usePasswordStrengthCheck from '../../../hooks/usePasswordStrengthCheck';

interface PasswordStrengthProps {
  password?: string;
}

const PasswordStrength = ({ password }: PasswordStrengthProps) => {
  const passwordStrengthCheck = usePasswordStrengthCheck(password);

  return (
    <List
      aria-live="polite"
      id="password-list"
      sx={{ pt: { mobile: 1, tablet: 2 }, pb: 0 }}
    >
      <ListItem disablePadding>
        <ListItemIcon sx={{ minWidth: 24 }}>
          {passwordStrengthCheck.strong ? (
            <CheckIcon aria-label="Passing" />
          ) : (
            <FailedIcon aria-label="Failing" />
          )}
        </ListItemIcon>
        <Typography
          sx={{
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            fontSize: { mobile: 14, tablet: 16 },
            fontWeight: 400,
            color: colors.csaaDark,
          }}
          data-testid="password-strength-text"
        >
          Password strength: {passwordStrengthCheck.strong ? 'strong' : 'weak'}.
        </Typography>
      </ListItem>
      <ListItem disablePadding sx={{ pt: { mobile: 0.5, tablet: 1 } }}>
        <ListItemIcon sx={{ minWidth: 24 }}>
          {passwordStrengthCheck.upperAndLowercaseTest ? (
            <CheckIcon
              aria-label="Passing, contains"
              data-testid="success-icon-upper-lower-case"
            />
          ) : (
            <FailedIcon
              aria-label="Failing, does not contain"
              data-testid="failing-icon-upper-lower-case"
            />
          )}
        </ListItemIcon>
        <Typography
          sx={{
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            fontSize: { mobile: 14, tablet: 16 },
            fontWeight: 400,
            color: colors.csaaDark,
          }}
        >
          Upper and lowercase letters.
        </Typography>
      </ListItem>
      <ListItem disablePadding sx={{ pt: { mobile: 0.5, tablet: 1 } }}>
        <ListItemIcon sx={{ minWidth: 24 }}>
          {passwordStrengthCheck.numberTest ? (
            <CheckIcon
              aria-label="Passing, contains"
              data-testid="success-icon-contains-number"
            />
          ) : (
            <FailedIcon
              aria-label="Failing, does not contain"
              data-testid="failing-icon-contains-number"
            />
          )}
        </ListItemIcon>
        <Typography
          sx={{
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            fontSize: { mobile: 14, tablet: 16 },
            fontWeight: 400,
            color: colors.csaaDark,
          }}
        >
          At least 1 number.
        </Typography>
      </ListItem>
      <ListItem disablePadding sx={{ pt: { mobile: 0.5, tablet: 1 } }}>
        <ListItemIcon sx={{ minWidth: 24 }}>
          {passwordStrengthCheck.lengthTest ? (
            <CheckIcon
              aria-label="Passing, contains"
              data-testid="success-icon-length"
            />
          ) : (
            <FailedIcon
              aria-label="Failing, does not contain"
              data-testid="failing-icon-length"
            />
          )}
        </ListItemIcon>
        <Typography
          sx={{
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            fontSize: { mobile: 14, tablet: 16 },
            fontWeight: 400,
            color: colors.csaaDark,
          }}
        >
          8-64 characters, no space.
        </Typography>
      </ListItem>
    </List>
  );
};

export default PasswordStrength;
